import { redirect } from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { api } from "@/lib/api";
import { queries } from "@/lib/queries";
import { readFresh } from "@/lib/query";
import { requireSession } from "@/lib/guards";
import { errorMessage } from "@/lib/session";
import { toast } from "@/lib/toast";

/**
 * Leaving a space, from the dialog its menu opens. No page of its own:
 * reached by fetchers, the loader for how many of the caller's results are
 * shared there — asked when the dialog opens, not with every visit to the
 * feed — and the action for leaving, their results taken off its boards
 * when they ticked the box.
 */

export type LeavingData = { sharedResults: number | null };

export async function leavingLoader({ request, params }: LoaderFunctionArgs): Promise<LeavingData> {
  await requireSession(request);
  // A fetcher's loader that throws takes the page with it, to the error
  // boundary: a count that fails is no reason to lose the feed. The dialog
  // says it could not count, and leaving still works. A fetcher keeps what
  // its loader answered and is never redrawn by a read behind a copy, and
  // the count decides what leaving takes off the boards: read fresh.
  try {
    const { sharedResults } = await readFresh(queries.leaving(params.slug ?? ""));
    return { sharedResults };
  } catch {
    return { sharedResults: null };
  }
}

export async function leaveAction({ request, params }: ActionFunctionArgs) {
  const form = await request.formData();
  try {
    await api.space(params.slug ?? "").leave(form.get("unshare") === null ? {} : { unshare: true });
  } catch (error) {
    return { error: errorMessage(error, "Could not leave the space") };
  }
  // Said by name: the spaces page shows it only by its absence.
  toast(`You left ${String(form.get("name") ?? "") || "the space"}.`);
  return redirect("/spaces");
}
